import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { enqueue, isNetworkError } from "@/lib/offlineQueue";

export interface Playlist {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  item_count?: number;
  cover_image_url?: string | null;
  cover_generation_count?: number;
}

export interface PlaylistItem {
  id: string;
  playlist_id: string;
  episode_id: string;
  position: number;
  note: string | null;
  created_at: string;
  episode?: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    published_at: string | null;
    duration_seconds: number | null;
    audio_url: string | null;
    spotify_episode_url: string | null;
    apple_episode_url: string | null;
    podcast: {
      name: string;
      artwork_url: string | null;
    };
  };
}

function generateSlug(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") +
    "-" +
    Math.random().toString(36).slice(2, 8)
  );
}

export function usePlaylists() {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingCovers, setGeneratingCovers] = useState<Set<string>>(new Set());

  const fetchPlaylists = useCallback(async () => {
    if (!user) {
      setPlaylists([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("playlists")
      .select("*, playlist_items(id)")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false }) as any;

    if (data) {
      setPlaylists(
        (data as any[]).map((p) => ({
          ...p,
          item_count: p.playlist_items?.length || 0,
          playlist_items: undefined,
        }))
      );
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPlaylists();
  }, [fetchPlaylists]);

  const fetchPlaylistItems = useCallback(
    async (playlistId: string): Promise<PlaylistItem[]> => {
      const { data } = await supabase
        .from("playlist_items")
        .select("*, episode:episodes(id, title, slug, description, published_at, duration_seconds, audio_url, spotify_episode_url, apple_episode_url, podcast:podcasts(name, artwork_url))")
        .eq("playlist_id", playlistId)
        .order("position", { ascending: true });
      return (data as unknown as PlaylistItem[]) || [];
    },
    []
  );

  const reorderItems = useCallback(
    async (playlistId: string, items: PlaylistItem[]) => {
      // Update positions in DB
      const updates = items.map((item, idx) =>
        supabase
          .from("playlist_items")
          .update({ position: idx })
          .eq("id", item.id)
      );
      await Promise.all(updates);
    },
    []
  );

  const createPlaylist = useCallback(
    async (title: string, description?: string) => {
      if (!user) return null;
      const slug = generateSlug(title);
      const { data, error } = await supabase
        .from("playlists")
        .insert({ user_id: user.id, title, slug, description: description || null, is_public: true })
        .select()
        .single();
      if (error) {
        console.error("Failed to create playlist:", error);
        return null;
      }
      const playlist = { ...(data as Playlist), item_count: 0 };
      setPlaylists((prev) => [playlist, ...prev]);
      return playlist;
    },
    [user]
  );

  const deletePlaylist = useCallback(
    async (playlistId: string) => {
      if (!user) return;
      await supabase.from("playlists").delete().eq("id", playlistId);
      setPlaylists((prev) => prev.filter((p) => p.id !== playlistId));
    },
    [user]
  );

  const updatePlaylist = useCallback(
    async (playlistId: string, updates: { title?: string; description?: string; is_public?: boolean }) => {
      if (!user) return;
      await supabase.from("playlists").update(updates).eq("id", playlistId);
      setPlaylists((prev) =>
        prev.map((p) => (p.id === playlistId ? { ...p, ...updates } : p))
      );
    },
    [user]
  );

  const addEpisode = useCallback(
    async (playlistId: string, episodeId: string) => {
      if (!user) return false;

      // Optimistic count bump so the UI updates instantly; rolled back on error.
      setPlaylists((prev) =>
        prev.map((p) =>
          p.id === playlistId ? { ...p, item_count: (p.item_count || 0) + 1 } : p
        )
      );

      const { error } = await supabase
        .from("playlist_items")
        .insert({ playlist_id: playlistId, episode_id: episodeId });
      if (error) {
        if (isNetworkError(error) || !navigator.onLine) {
          await enqueue({
            kind: "playlist_item",
            payload: { op: "add", playlist_id: playlistId, episode_id: episodeId },
            dedupKey: `playlist-add:${playlistId}:${episodeId}`,
          });
          return true;
        }
        setPlaylists((prev) =>
          prev.map((p) =>
            p.id === playlistId
              ? { ...p, item_count: Math.max(0, (p.item_count || 0) - 1) }
              : p
          )
        );
        if (error.code === "23505") return false; // duplicate, silently ignore
        console.error("Failed to add episode:", error);
        return false;
      }

      // Check current item count from DB to decide if we should auto-generate cover
      const { count } = await supabase
        .from("playlist_items")
        .select("id", { count: "exact", head: true })
        .eq("playlist_id", playlistId);

      if (count === 1) {
        // First episode just added — check if cover already exists
        const { data: pl } = await supabase
          .from("playlists")
          .select("cover_image_url")
          .eq("id", playlistId)
          .single() as any;

        if (!pl?.cover_image_url) {
          console.log("Auto-generating cover for playlist", playlistId);
          setGeneratingCovers((prev) => new Set(prev).add(playlistId));
          supabase.functions
            .invoke("generate-playlist-cover", { body: { playlist_id: playlistId } })
            .then(({ data }) => {
              if (data?.cover_image_url) {
                setPlaylists((prev) =>
                  prev.map((p) =>
                    p.id === playlistId
                      ? { ...p, cover_image_url: data.cover_image_url, cover_generation_count: data.generation_count }
                      : p
                  )
                );
              }
            })
            .catch((e) => console.error("Auto cover generation failed:", e))
            .finally(() => {
              setGeneratingCovers((prev) => {
                const next = new Set(prev);
                next.delete(playlistId);
                return next;
              });
            });
        }
      }

      return true;
    },
    [user]
  );

  const removeEpisode = useCallback(
    async (playlistId: string, episodeId: string) => {
      if (!user) return;

      // Optimistic count decrement; restored on error.
      setPlaylists((prev) =>
        prev.map((p) =>
          p.id === playlistId ? { ...p, item_count: Math.max(0, (p.item_count || 0) - 1) } : p
        )
      );

      const { error } = await supabase
        .from("playlist_items")
        .delete()
        .eq("playlist_id", playlistId)
        .eq("episode_id", episodeId);
      if (error) {
        if (isNetworkError(error) || !navigator.onLine) {
          await enqueue({
            kind: "playlist_item",
            payload: { op: "remove", playlist_id: playlistId, episode_id: episodeId },
            dedupKey: `playlist-remove:${playlistId}:${episodeId}`,
          });
          return;
        }
        setPlaylists((prev) =>
          prev.map((p) =>
            p.id === playlistId ? { ...p, item_count: (p.item_count || 0) + 1 } : p
          )
        );
        console.error("Failed to remove episode:", error);
      }
    },
    [user]
  );

  return {
    playlists,
    loading,
    generatingCovers,
    createPlaylist,
    deletePlaylist,
    updatePlaylist,
    addEpisode,
    removeEpisode,
    fetchPlaylistItems,
    reorderItems,
    refetch: fetchPlaylists,
  };
}

/** Fetch a single public playlist by slug (no auth required) */
export async function fetchPublicPlaylist(slug: string) {
  const { data: playlist, error } = await supabase
    .from("playlists")
    .select("*")
    .eq("slug", slug)
    .eq("is_public", true)
    .single();

  if (error || !playlist) return null;

  const [itemsResult, profileResult] = await Promise.all([
    supabase
      .from("playlist_items")
      .select("*, episode:episodes(id, title, slug, description, published_at, duration_seconds, audio_url, spotify_episode_url, apple_episode_url, podcast:podcasts(name, artwork_url))")
      .eq("playlist_id", playlist.id)
      .order("position", { ascending: true }),
    supabase
      .from("profiles")
      .select("display_name, avatar_url")
      .eq("user_id", playlist.user_id)
      .single(),
  ]);

  return {
    ...playlist,
    items: (itemsResult.data as unknown as PlaylistItem[]) || [],
    creator: profileResult.data || null,
  };
}

